import React, { useState, useEffect, useCallback } from 'react';
import '../styles/react-colorful.css';
import { HexColorPicker } from 'react-colorful';
import { useRef } from 'react';
import { VideoPost } from '../services/videoPostService';
import videoPostService from '../services/videoPostService';
import VideoPostItem from './VideoPostItem';
import { Device } from '../services/deviceService';
import deviceService from '../services/deviceService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface VideoPostListProps {
  device: Device;
}

export const VideoPostList: React.FC<VideoPostListProps> = ({ device }) => {
  const [videoPosts, setVideoPosts] = useState<VideoPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelType, setNewChannelType] = useState<'twitch' | 'youtube' | 'kick'>('youtube');
  const [newColor, setNewColor] = useState('#ff0000');
  const [newBlink, setNewBlink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingVideoPostId, setDeletingVideoPostId] = useState<number | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadVideoPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const deviceVideoPosts = await videoPostService.getByDeviceId(device.id);
      // Sort by priority (lower number = higher priority = shown first)
      const sorted = deviceVideoPosts.sort((a, b) => a.priority - b.priority);
      setVideoPosts(sorted);
    } catch (err: any) {
      console.error('Failed to load video posts:', err);
      setError('Failed to load video posts');
      setVideoPosts([]);
    } finally {
      setLoading(false);
    }
  }, [device.id]);

  useEffect(() => {
    loadVideoPosts();
  }, [loadVideoPosts]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showColorPicker && pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setNewChannelId('');
    setNewChannelType('youtube');
    setNewColor('#ff0000');
    setNewBlink(false);
    setError('');
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewChannelId('');
    setNewColor('#ff0000');
    setNewBlink(false);
    setShowColorPicker(false);
    setError('');
  };

  const handleAddVideoPost = async () => {
    if (!newChannelId.trim()) {
      setError('Channel ID is required');
      return;
    }

    if (videoPosts.length >= 5) {
      setError('Maximum of 5 video posts per device reached');
      return;
    }

    // For Single Color devices, use white (255, 255, 255)
    // For RGB devices, convert hex color to RGB
    let r = 255, g = 255, b = 255;
    
    if (device.device_type === 'RGB') {
      const hex = newColor.replace('#', '');
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }

    setError('');
    setCreating(true);

    try {
      const newVideoPost = await videoPostService.create(device.id, newChannelId, newChannelType, r, g, b, newBlink);
      setVideoPosts([...videoPosts, newVideoPost]);
      handleCloseAddModal();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create video post';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handlePreview = async () => {
    setError('');
    setPreviewing(true);

    try {
      if (device.device_type === 'RGB') {
        // For RGB devices, convert hex color to RGB
        const hex = newColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        await deviceService.previewColor(device.id, r, g, b, newBlink);
      } else {
        // For non-RGB devices, use status: 0 = off, 1 = on, 2 = blinking
        const status = newBlink ? 2 : 1;
        await deviceService.previewLight(device.id, status as 0 | 1 | 2);
      }
      console.log('Preview sent to device');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to preview on device';
      setError(message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleDeleteVideoPost = async (videoPostId: number) => {
    if (!window.confirm('Are you sure you want to delete this video post notification?')) {
      return;
    }

    setError('');
    setDeletingVideoPostId(videoPostId);

    try {
      await videoPostService.delete(videoPostId);
      setVideoPosts(videoPosts.filter(v => v.id !== videoPostId));
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to delete video post';
      setError(message);
    } finally {
      setDeletingVideoPostId(null);
    }
  };

  const handleUpdateVideoPost = (updatedVideoPost: VideoPost) => {
    setVideoPosts(videoPosts.map(v => v.id === updatedVideoPost.id ? updatedVideoPost : v));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = videoPosts.findIndex(v => v.id === active.id);
    const newIndex = videoPosts.findIndex(v => v.id === over.id);

    const newVideoPosts = arrayMove(videoPosts, oldIndex, newIndex);
    setVideoPosts(newVideoPosts);

    // Update priorities in background without refreshing
    const updates = newVideoPosts.map((videoPost, index) => {
      const newPriority = index + 1;
      return videoPostService.update(videoPost.id, { priority: newPriority });
    });

    try {
      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to update priorities:', err);
      setError('Failed to update priorities');
    }
  };

  return (
    <div className="stream-list-container" style={{ marginTop: 30 }}>
      <h3 style={{ marginBottom: 16, fontSize: 20, fontWeight: 600, color: 'var(--text-primary)' }}>
        📹 Video Post Notifications
      </h3>
      
      {videoPosts.length >= 5 && (
        <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>
          ⚠️ Maximum of 5 video posts per device reached
        </div>
      )}

      {/* Add Video Post Button */}
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={handleOpenAddModal}
          disabled={videoPosts.length >= 5}
          className="btn-add-stream"
          type="button"
          title={videoPosts.length >= 5 ? 'Maximum 5 video posts per device' : 'Add a new video post notification'}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {videoPosts.length >= 5 ? '⚠️ Max Video Posts (5)' : '+ Add Video Post Notification'}
        </button>
      </div>

      {/* Add Video Post Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCloseAddModal}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              padding: 30,
              borderRadius: 12,
              maxWidth: 450,
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 20, color: 'var(--text-primary)' }}>
              Add Video Post Notification
            </h3>

            {error && <div className="error-message" style={{ marginBottom: 15 }}>{error}</div>}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                Platform
              </label>
              <select
                value={newChannelType}
                onChange={(e) => setNewChannelType(e.target.value as 'twitch' | 'youtube' | 'kick')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
                disabled={true}
              >
                <option value="youtube">YouTube</option>
              </select>
              {newChannelType === 'youtube' && (
                <div style={{ 
                  marginTop: 10, 
                  padding: '10px', 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-color)',
                  borderRadius: 6,
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5'
                }}>
                  💡 <strong>Tip:</strong> Get YouTube channel ID at{' '}
                  <a 
                    href="https://yt-link.com/stats" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#667eea', textDecoration: 'underline' }}
                  >
                    yt-link.com/stats
                  </a>
                  {' '}by searching for the channel, then expand the arrow underneath the channel name.
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                Channel ID
              </label>
              <input
                type="text"
                value={newChannelId}
                onChange={(e) => setNewChannelId(e.target.value)}
                placeholder="Enter channel ID"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
                disabled={creating}
              />
            </div>

            {device.device_type === 'RGB' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                  LED Color
                </label>
                <HexColorPicker color={newColor} onChange={setNewColor} style={{ width: '100%' }} />
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: newColor,
                      border: '2px solid var(--border-color)',
                    }}
                  />
                  <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {newColor.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={newBlink}
                  onChange={(e) => setNewBlink(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600 }}>Enable Blinking</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={handlePreview}
                disabled={creating || previewing}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#667eea',
                  border: '1px solid #667eea',
                  borderRadius: 6,
                  cursor: (creating || previewing) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
                title="Preview this color on your device"
              >
                {previewing ? '⏳ Previewing...' : '👁️ Preview'}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleCloseAddModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddVideoPost}
                  disabled={creating}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: creating ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {creating ? 'Adding...' : 'Add Video Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Video Posts List */}
      {loading ? (
        <div className="loading-streams">Loading video posts...</div>
      ) : videoPosts.length === 0 ? (
        <div className="no-streams">No video post notifications yet. Create one above!</div>
      ) : (
        <div className="streams-list">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
            💡 Drag video posts to reorder priority (top = highest priority)
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={videoPosts.map(v => v.id)}
              strategy={verticalListSortingStrategy}
            >
              {videoPosts.map((videoPost, index) => (
                <VideoPostItem
                  key={videoPost.id}
                  videoPost={videoPost}
                  onDelete={handleDeleteVideoPost}
                  onUpdate={handleUpdateVideoPost}
                  isDeleting={deletingVideoPostId === videoPost.id}
                  priorityRank={index + 1}
                  deviceId={device.id}
                  deviceType={device.device_type}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default VideoPostList;
